import { useTheme } from '@mui/material';
import { AdviceBox } from '@portals/core/src/components/AdviceBox/AdviceBox';
import { Button } from '@portals/core/src/components/Button/Button';
import { LoadingDots } from '@portals/core/src/components/LoadingDots/LoadingDots';
import { MenuLink } from '@portals/core/src/components/MenuLink/MenuLink';
import { ButtonOptions, FlatTree, LinkOptions } from '@portals/core/src/components/Navigation/flatTree';
import { Link } from '@portals/core/src/components/Navigation/Link';
import { Navigation as PortalsNavigation } from '@portals/core/src/components/Navigation/Navigation';
import { useNavigation } from '@portals/core/src/components/Navigation/Provider';
import type { Theme } from '@portals/core/src/themes/themes';
import { ChevronRight, ChevronRightSmall, InfoCircleOutline } from '@portals/icons';
import { TrackingContext } from '@portals/tracking/src/context/tracking';
import { useRouter } from 'next/router';
import React, { forwardRef, ReactElement, useCallback, useContext, useEffect, useMemo } from 'react';

import { HeaderNavigation, NavigationItem } from '../../../models';
import type { Node } from '../../consumers/Consumer/Consumer';
import { useRequestHeaders } from '../../contexts/request-headers';
import { useServiceBoxContext } from '../../contexts/service-box';
import { useLinkProps } from '../../hooks/useLinkProps';
import { loadIcon } from '../../utils/loadIcon';
import { AccessibilityLinks } from '../AccessibilityLinks/AccessibilityLinks';
import { InternIconLink } from '../InternIconLink/InternIconLink';

type LinkType = NavigationItem['type'];

const RenderButton = forwardRef<HTMLButtonElement, ButtonOptions & RenderLinkProps>(
  ({ type, link, name, id, isLast, isActive, nextTab }, ref) => {
    const linkMap = {
      INTERNAL: {
        __component: 'link.internal-link',
        id: name,
        text: name,
        reference: {
          id: id,
          slugPath: link,
          __contentType: 'page',
        },
      },
      EXTERNAL: {
        __component: 'link.external-link',
        id: name,
        text: name,
        url: link,
      },
    };

    const { singleProps } = useLinkProps(linkMap[type.toString()]);
    const theme = useTheme();

    const onClick = useCallback(
      (event) => {
        if (!isLast) {
          nextTab();
        } else {
          singleProps.onClick(event);
        }
      },
      [isLast, singleProps, nextTab]
    );

    return (
      <Button
        key={id}
        id={`navigation-tab-${id}`}
        variant="tab"
        aria-selected={isActive}
        aria-labelledby={name}
        size="small"
        onClick={onClick}
        role="tab"
        tabIndex={0}
        sx={{
          color: theme.palette.text.primary,
          '&::after': { backgroundColor: theme.palette.text.primary },
        }}
        ref={ref}
      >
        {name}
      </Button>
    );
  }
);

RenderButton.displayName = 'NavigationRenderButton';

interface RenderLinkProps {
  link: string;
  name: string;
  type: LinkType;
  id: number;
}

/**
 * This does not useLinkProps, due optimisation.
 */
const RenderLink = forwardRef<unknown, LinkOptions & RenderLinkProps>(
  ({ type, link, name, isLast, isActive, nextPanel, variant }, ref) => {
    const trackingContext = useContext(TrackingContext);
    const { internalHost } = useRequestHeaders();
    const router = useRouter();

    let singleProps;
    if (type.toString() === 'EXTERNAL') {
      singleProps = {
        component: 'a',
        tabIndex: 0,
        role: 'link',
        href: link,
        target: '_blank',
        noopener: true,
        noreferrer: true,

        onClick: (event) => {
          event.stopPropagation();
          trackingContext?.trackLink?.({
            linkText: name,
            linkHref: link,
            linkType: 'LinkExternalLink',
          });
        },
      };
    } else {
      const internalLink = link === '/' ? link : `${link}.html`;

      singleProps = {
        component: 'a',
        tabIndex: 0,
        href: `${internalHost || ''}${internalLink}`,
        onClick: (event) => {
          trackingContext?.trackLink?.({
            linkHref: link,
            linkText: name,
            linkType: 'LinkMenuLink',
          });

          event.preventDefault();

          if (!isLast) {
            nextPanel(event);
          } else {
            router.push(internalLink).catch((e) => {
              // eslint-disable-next-line no-console
              console.error(e);
            });
          }

          return false;
        },
      };
    }

    if (variant === 'mobile') {
      return (
        <MenuLink {...singleProps} underlined={isActive} ref={ref} endIcon={!isLast && <ChevronRightSmall />}>
          {name}
        </MenuLink>
      );
    }

    return (
      <Link {...singleProps} ref={ref} underlined={isActive} icon={!isLast && <ChevronRight />}>
        {name}
      </Link>
    );
  }
);

RenderLink.displayName = 'NavigationRenderLink';

export interface NavigationProps {
  node: HeaderNavigation;
  createHeaderTheme: () => Theme;
  renderNode: (node: Node) => ReactElement;
}

export const Navigation: React.FC<NavigationProps> = ({ node, createHeaderTheme, renderNode }) => {
  const serviceBoxContext = useServiceBoxContext();
  const { navigation, headerBar } = node;
  const router = useRouter();
  const navigationContext = useNavigation();

  useEffect(() => {
    const handleRouteChange = () => {
      navigationContext?.setOpen(false);
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router, navigationContext]);

  const MobileProps = useMemo(() => {
    function renderCustomPanel() {
      if (!serviceBoxContext?.open) {
        return null;
      }

      if (serviceBoxContext?.error) {
        return (
          <AdviceBox variant="info" icon={<InfoCircleOutline />}>
            Es ist ein Fehler aufgetreten, bitte versuchen Sie es in Kürze erneut.
          </AdviceBox>
        );
      }

      if (!serviceBoxContext?.data) {
        return <LoadingDots />;
      }

      return renderNode(serviceBoxContext?.data);
    }

    return {
      customPanel: renderCustomPanel(),
      onCustomPanelBack: serviceBoxContext?.onClose,
    };
  }, [
    serviceBoxContext?.onClose,
    serviceBoxContext?.open,
    serviceBoxContext?.error,
    serviceBoxContext?.data,
    renderNode,
  ]);

  if (!navigation?.items?.length || !headerBar) {
    return null;
  }

  const newItems: FlatTree[] = navigation.items.map((item) => ({
    ...item,
    link: (props: LinkOptions) => (
      <RenderLink key={item.id} type={item.type} link={item.link} name={item.name} id={item.related} {...props} />
    ),
    button: (props: ButtonOptions) => (
      <RenderButton key={item.id} type={item.type} link={item.link} name={item.name} id={item.related} {...props} />
    ),
  }));

  const MobileIcon = loadIcon(process.env.TENANT_ICON);

  return (
    <PortalsNavigation
      onClose={serviceBoxContext?.onClose}
      MobileProps={MobileProps}
      tree={newItems}
      HeaderBarProps={{
        mobileIcon: <MobileIcon fontSize="inherit" />,
        id: headerBar.id,
        level1Navigation: renderNode(headerBar.level1Navigation),
        accessibilityLinks: <AccessibilityLinks node={headerBar.accessibilityLinks} renderNode={renderNode} />,
        searchButton: headerBar.searchButton && (
          <InternIconLink key={headerBar.searchButton.id} node={headerBar.searchButton} />
        ),
        button: renderNode(headerBar.button),
        menuButton: headerBar.menuButton && (
          <InternIconLink key={headerBar.menuButton.id} node={headerBar.menuButton} />
        ),
      }}
      DesktopProps={{
        tabSize: headerBar.level2Navigation?.items.find((item) => item)?.size,
      }}
      createHeaderTheme={createHeaderTheme}
    />
  );
};
